import axiosLib from "axios";
import { checkAndRefreshToken, refreshToken } from "../utils/tokenUtils";

// 创建基础的 axios 实例用于刷新 token（避免循环依赖）
const baseAxios = axiosLib.create({
  xsrfCookieName: "csrftoken",
  xsrfHeaderName: "X-CSRFToken",
});

// 防止并发刷新 token
let isRefreshing = false;
let refreshSubscribers = [];

function subscribeTokenRefresh(cb) {
  refreshSubscribers.push(cb);
}

function onTokenRefreshed(token) {
  refreshSubscribers.forEach(cb => cb(token));
  refreshSubscribers = [];
}

export const axios = () => {
  let caxios = axiosLib.create({
    xsrfCookieName: "csrftoken",
    xsrfHeaderName: "X-CSRFToken",
  });

  if (
    window.location.pathname.endsWith("/embed") &&
    window.location.search.includes("_signature")
  ) {
    const searchParams = new URLSearchParams(window.location.search);
    const signature = searchParams.get("_signature");

    if (signature) {
      caxios.defaults.headers.common["Authorization"] =
        "Signature " + signature;
    }
    caxios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          // 对于embed页面，跳转到session-expired页面
          const currentPath = window.location.pathname;
          if (!currentPath.includes('/login') && !currentPath.includes('/session-expired')) {
          window.location.href = "/session-expired";
          }
        }
        return Promise.reject(error);
      },
    );
  } else {

    // 添加请求拦截器
    caxios.interceptors.request.use(async function (config) {
      // 在发送请求之前检查并刷新 token（如果需要）
      // 注意：这里不能使用 caxios，否则会造成循环依赖
      // 使用原生 fetch 或基础 axios 实例
      const token = await checkAndRefreshToken(() => baseAxios);
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
      return config;
    }, function (error) {
      // 对请求错误做些什么
      return Promise.reject(error);
    });

    caxios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        const currentPath = window.location.pathname;
        
        // 定义不需要跳转的路径
        const excludedPaths = [
          "/login",
          "/signup",
          "/session-expired",
        ];
        const excludedPrefixes = [
          "/s/",
          "/a/",
          "/hub",
          "/app/",
          "/embed"
        ];
        
        // 检查当前路径是否需要排除
        const shouldExclude = excludedPaths.includes(currentPath) ||
          excludedPrefixes.some(prefix => currentPath.startsWith(prefix));
        
        // 如果是 401 或 403 错误
        if (error.response && 
            (error.response.status === 401 || error.response.status === 403)) {
          
          // 如果是刷新 token 的请求失败，直接跳转到登录页
          if (originalRequest && originalRequest.url && 
              originalRequest.url.includes('/user/refresh_token')) {
            localStorage.removeItem('jwtToken');
            if (!shouldExclude) {
              const redirectUrl = encodeURIComponent(currentPath + window.location.search);
              window.location.href = `/login?redirectUrl=${redirectUrl}`;
            }
            return Promise.reject(error);
          }
          
          // 如果还没有重试过，尝试刷新 token
          if (originalRequest && !originalRequest._retry) {
          // 如果正在刷新 token，等待刷新完成
          if (isRefreshing) {
            return new Promise((resolve) => {
              subscribeTokenRefresh((token) => {
                originalRequest.headers['Authorization'] = `Bearer ${token}`;
                resolve(caxios(originalRequest));
              });
            });
          }
          
          // 标记正在刷新
          originalRequest._retry = true;
          isRefreshing = true;
          
          try {
            // 尝试刷新 token（使用基础 axios 实例避免循环依赖）
            const newToken = await refreshToken(() => baseAxios);
            
            if (newToken) {
              // 刷新成功，通知所有等待的请求
              onTokenRefreshed(newToken);
              
              // 重试原始请求
              originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
              return caxios(originalRequest);
            } else {
              // 刷新失败，跳转到登录页
              localStorage.removeItem('jwtToken');
                if (!shouldExclude) {
                  const redirectUrl = encodeURIComponent(currentPath + window.location.search);
                  window.location.href = `/login?redirectUrl=${redirectUrl}`;
              }
              return Promise.reject(error);
            }
          } catch (refreshError) {
            // 刷新失败
            isRefreshing = false;
            localStorage.removeItem('jwtToken');
              if (!shouldExclude) {
                const redirectUrl = encodeURIComponent(currentPath + window.location.search);
                window.location.href = `/login?redirectUrl=${redirectUrl}`;
            }
            return Promise.reject(refreshError);
          } finally {
            isRefreshing = false;
            }
          } else {
            // 已经重试过或无法重试，直接跳转到登录页
            localStorage.removeItem('jwtToken');
            if (!shouldExclude) {
              const redirectUrl = encodeURIComponent(currentPath + window.location.search);
              window.location.href = `/login?redirectUrl=${redirectUrl}`;
            }
            return Promise.reject(error);
          }
        }

        return Promise.reject(error);
      },
    );
  }

  return caxios;
};

